import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Route, Routes, useNavigate } from "react-router-dom";

import { ApiError, api } from "../../api/client";
import { HierarchyApi } from "../../admin/hierarchy/api";
import { RbacApi, type AppUser } from "../../admin/rbac/api";
import { useScopeNames } from "../../admin/hierarchy/useScopeNames";
import { useUsersById } from "../../admin/rbac/useUsersById";
import { useMarkSeen } from "../../notifications/useMarkSeen";
import { SearchableSelect } from "../../shell/SearchableSelect";
import { ResolutionPanel } from "../../shell/ResolutionPanel";
import { AssignAsTaskButton } from "../../shell/AssignAsTaskButton";
import { Wizard, WizardField, WizardSection, ScopePicker, type WizardStep } from "../../wizard/Wizard";
import {
  PopularityApi,
  type Classification,
  type PopularityReport,
  type PopularityScore,
  type ScopeLevel,
  type SubjectType,
} from "./api";

export function PopularityRoutes() {
  return (
    <Routes>
      <Route index element={<List />} />
      <Route path="new" element={<NewReport />} />
    </Routes>
  );
}

function List() {
  const [score, setScore] = useState<PopularityScore | "">("");
  const [subjectType, setSubjectType] = useState<SubjectType | "">("");
  const [search, setSearch] = useState("");

  const data = useQuery({
    queryKey: ["popularity", score, subjectType],
    queryFn: () =>
      PopularityApi.list({
        score: (score || undefined) as PopularityScore | undefined,
        subjectType: (subjectType || undefined) as SubjectType | undefined,
      }),
  });
  useMarkSeen("popularity", data.dataUpdatedAt);

  const { name: userName } = useUsersById();
  const { branchName } = useScopeNames();

  function subjectLabel(r: { subjectType: SubjectType; subjectUid?: string | null; subjectName?: string | null }): string {
    if (r.subjectType === "internal") return userName(r.subjectUid);
    return r.subjectName ?? "—";
  }

  const items = (data.data?.items ?? [])
    .filter((r) => !search || (subjectLabel(r) + " " + r.observations).toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));

  return (
    <div>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Capture Popular Persons</h1>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>Capture how well-known or supported a person is in your area — what people on the ground are saying.</p>
        </div>
        <Link to="new"><button>New report</button></Link>
      </header>

      <div className="row-3" style={{ marginTop: 16 }}>
        <label>
          Score:
          <select value={score} onChange={(e) => setScore(e.target.value as PopularityScore | "")}>
            <option value="">All</option>
            <option value="very_high">Very high</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="very_low">Very low</option>
          </select>
        </label>
        <label>
          Subject type:
          <select value={subjectType} onChange={(e) => setSubjectType(e.target.value as SubjectType | "")}>
            <option value="">All</option>
            <option value="internal">Internal (registered user)</option>
            <option value="external">External (free-text)</option>
          </select>
        </label>
        <label>
          Search:
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Subject or observation…" />
        </label>
      </div>

      {data.isLoading && <p>Loading…</p>}
      {data.error && <p className="error">{(data.error as Error).message}</p>}

      <div className="card-grid">
        {items.map((r) => (
          <PopCard
            key={r.id}
            report={r}
            label={subjectLabel(r)}
            userName={userName}
            branchName={branchName}
          />
        ))}
        {items.length === 0 && !data.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>No matching reports.</div>
        )}
      </div>
    </div>
  );
}

function PopCard({
  report, label, userName, branchName,
}: {
  report: PopularityReport;
  label: string;
  userName: (uid: string | null | undefined) => string;
  branchName: (id: string | null | undefined) => string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="list-card" onClick={() => setOpen(true)} style={{ cursor: "pointer" }}>
        <div className="list-card-header">
          <ScoreBadge score={report.score} />
          <span className="muted" style={{ fontSize: 11 }}>{report.subjectType}</span>
        </div>
        <div className="list-card-title">{label}</div>
        <div className="list-card-snippet">{report.observations}</div>
        <div className="list-card-foot">
          <span>{report.scopeLevel === "branch" ? branchName(report.scopeId) : report.scopeLevel}</span>
          <span>{report.createdAt ? new Date(report.createdAt).toLocaleDateString() : "—"}</span>
        </div>
      </div>
      {open && (
        <div role="dialog" aria-modal="true"
          onClick={(e) => { if (e.currentTarget === e.target) setOpen(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}>
          <div className="card" style={{ maxWidth: 640, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <ScoreBadge score={report.score} />
                <h2 style={{ margin: "8px 0 0", fontSize: 18 }}>{label}</h2>
                <div className="muted" style={{ fontSize: 12 }}>
                  {report.scopeLevel} · {report.createdAt ? new Date(report.createdAt).toLocaleString() : "—"}
                </div>
              </div>
              <button className="ghost" onClick={() => setOpen(false)}>Close</button>
            </div>
            <dl className="kv" style={{ marginTop: 16 }}>
              <dt>Observations</dt><dd style={{ whiteSpace: "pre-wrap" }}>{report.observations}</dd>
              {report.subjectType === "external" && report.subjectContext && (
                <><dt>Context</dt><dd>{report.subjectContext}</dd></>
              )}
              <dt>Filed by</dt><dd>{userName(report.reporterUid)}</dd>
              <dt>Classification</dt><dd>{report.classification}</dd>
            </dl>
            <ResolutionPanel
              collection="popularityReports"
              recordId={report.id}
              current={(report as { resolutionStatus?: "pending" | "in_progress" | "resolved" | "false_alarm" }).resolutionStatus}
            />
            <div style={{ marginTop: 12 }}>
              <AssignAsTaskButton
                relatedType="popularity"
                relatedId={report.id}
                suggestedTitle={`Action popularity report: ${label}`}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NewReport() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({ queryKey: ["users"], queryFn: RbacApi.listUsers });
  const scope = useQuery({ queryKey: ["my-scope"], queryFn: HierarchyApi.myScope });
  const [cid, setCid] = useState("");
  const [wid, setWid] = useState("");
  const [bid, setBid] = useState("");
  const constituencies = scope.data?.constituencies ?? [];
  const wardsForCid = (scope.data?.wards ?? []).filter((w) => w.constituencyId === cid);
  const branchesForWid = (scope.data?.branches ?? []).filter((b) => b.wardId === wid);

  const [subjectType, setSubjectType] = useState<SubjectType>("internal");
  const [subjectUid, setSubjectUid] = useState("");
  const [subjectPersonId, setSubjectPersonId] = useState("");
  const [subjectName, setSubjectName] = useState("");
  const [subjectContext, setSubjectContext] = useState("");
  // People (community contacts) for the third subject type — typically
  // headmen / pastors / community organisers being scored.
  const peopleQuery = useQuery({
    queryKey: ["people"],
    queryFn: () => api<{ items: { id: string; fullName: string; position: string | null }[] }>("/people"),
  });
  const peopleOptions = peopleQuery.data?.items ?? [];
  const [score, setScore] = useState<PopularityScore>("medium");
  const [observations, setObservations] = useState("");
  const [scopeLevel, setScopeLevel] = useState<ScopeLevel>("branch");
  const [classification, setClassification] = useState<Classification>("unclassified");

  const create = useMutation({
    mutationFn: PopularityApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["popularity"] });
      nav("..", { replace: true });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  const scopeId =
    scopeLevel === "branch" ? bid : scopeLevel === "ward" ? wid : cid;

  function submit() {
    if (!observations || !scopeId) return;
    if (subjectType === "internal" && !subjectUid) return;
    if (subjectType === "person" && !subjectPersonId) return;
    if (subjectType === "external" && !subjectName) return;
    create.mutate({
      subjectType,
      subjectUid: subjectType === "internal" ? subjectUid : undefined,
      subjectPersonId: subjectType === "person" ? subjectPersonId : undefined,
      subjectName: subjectType === "external" ? subjectName : undefined,
      subjectContext: subjectType === "external" ? subjectContext : undefined,
      scopeLevel,
      scopeId,
      score,
      observations,
      classification,
    });
  }

  const userOptions = users.data?.items ?? [];
  const subjectReady =
    subjectType === "internal" ? !!subjectUid :
    subjectType === "person" ? !!subjectPersonId :
    !!subjectName;

  const steps: WizardStep[] = [
    {
      title: "Where",
      canNext: !!scopeId,
      blockerHint: "Pick a scope level + area",
      body: (
        <WizardSection title="Where do these observations come from?" hint="Branch is tightest — pick the smallest area the report applies to.">
          <WizardField label="Scope level">
            <select
              value={scopeLevel}
              onChange={(e) => { setScopeLevel(e.target.value as ScopeLevel); setWid(""); setBid(""); }}
            >
              <option value="branch">Branch</option>
              <option value="ward">Ward</option>
              <option value="constituency">Constituency</option>
            </select>
          </WizardField>
          <ScopePicker
            constituencies={constituencies}
            wards={wardsForCid}
            branches={branchesForWid}
            cid={cid} wid={wid} bid={bid}
            onCidChange={(v) => { setCid(v); setWid(""); setBid(""); }}
            onWidChange={(v) => { setWid(v); setBid(""); }}
            onBidChange={setBid}
            needBranch={scopeLevel === "branch"}
          />
        </WizardSection>
      ),
    },
    {
      title: "Subject",
      canNext: subjectReady,
      blockerHint:
        subjectType === "internal" ? "Pick a registered user"
          : subjectType === "person" ? "Pick a community contact"
          : "Subject name is required",
      body: (
        <WizardSection title="Who is this report about?">
          <WizardField label="Subject type">
            <select value={subjectType} onChange={(e) => setSubjectType(e.target.value as SubjectType)}>
              <option value="internal">Registered user (has an account)</option>
              <option value="person">Community contact (from People)</option>
              <option value="external">External — type a name</option>
            </select>
          </WizardField>
          {subjectType === "internal" && (
            <WizardField label="Subject" hint="Type to search registered users.">
              <SearchableSelect
                value={subjectUid}
                onChange={setSubjectUid}
                options={userOptions.map((u: AppUser) => ({
                  value: u.id,
                  label: u.fullName || u.email,
                  hint: u.email,
                }))}
                required
              />
            </WizardField>
          )}
          {subjectType === "person" && (
            <WizardField label="Subject" hint="Type to search community contacts captured under Setup → People.">
              <SearchableSelect
                value={subjectPersonId}
                onChange={setSubjectPersonId}
                options={peopleOptions.map((p) => ({
                  value: p.id,
                  label: p.fullName,
                  hint: p.position ?? "Community contact",
                }))}
                emptyHint="No People records yet. Add some under Setup → People."
                required
              />
            </WizardField>
          )}
          {subjectType === "external" && (
            <>
              <WizardField label="Subject name">
                <input value={subjectName} onChange={(e) => setSubjectName(e.target.value)} placeholder="e.g. Felix Kabwe" />
              </WizardField>
              <WizardField label="Context (optional)" hint="Who they are: party affiliation, role, etc.">
                <input value={subjectContext} onChange={(e) => setSubjectContext(e.target.value)} placeholder="e.g. opposition ward chair" />
              </WizardField>
            </>
          )}
        </WizardSection>
      ),
    },
    {
      title: "Score",
      canNext: true,
      body: (
        <WizardSection title="How popular are they here?" hint="Score reflects sentiment in the chosen area, not nationally.">
          <WizardField label="Score">
            <select value={score} onChange={(e) => setScore(e.target.value as PopularityScore)}>
              <option value="very_high">Very high</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="very_low">Very low</option>
            </select>
          </WizardField>
          <WizardField label="Classification">
            <select value={classification} onChange={(e) => setClassification(e.target.value as Classification)}>
              <option value="unclassified">Unclassified</option>
              <option value="restricted">Restricted</option>
              <option value="secret">Secret</option>
            </select>
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Observations",
      canNext: !!observations.trim(),
      blockerHint: "Observations are required",
      body: (
        <WizardSection title="What did you observe?" hint="Specific conversations, attendance numbers, public reactions. No speculation.">
          <WizardField label="Observations">
            <textarea value={observations} onChange={(e) => setObservations(e.target.value)} rows={6} placeholder="What you saw and heard." />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Review",
      canNext: subjectReady && !!observations.trim() && !!scopeId,
      body: (
        <WizardSection title="Review and file">
          <dl className="kv">
            <dt>Subject</dt><dd>
              {subjectType === "internal"
                ? userOptions.find((u) => u.id === subjectUid)?.fullName ?? subjectUid
                : `${subjectName}${subjectContext ? ` — ${subjectContext}` : ""}`}
            </dd>
            <dt>Score</dt><dd>{score.replace("_", " ")}</dd>
            <dt>Scope</dt><dd>{scopeLevel} / {scopeId}</dd>
            <dt>Classification</dt><dd>{classification}</dd>
            <dt>Observations</dt><dd style={{ whiteSpace: "pre-wrap" }}>{observations}</dd>
          </dl>
        </WizardSection>
      ),
    },
  ];

  return (
    <div>
      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      <Wizard
        title="Capture Popular Persons"
        subtitle="Subject can be a registered user or anyone you can identify by name."
        steps={steps}
        busy={create.isPending}
        submitLabel="File report"
        onSubmit={submit}
        onCancel={() => nav("..")}
      />
    </div>
  );
}

function ScoreBadge({ score }: { score: PopularityScore }) {
  const colors: Record<PopularityScore, string> = {
    very_high: "#3a8053",
    high: "#3a8053",
    medium: "#a87b1c",
    low: "#c0531a",
    very_low: "#b14444",
  };
  return (
    <span style={{
      marginLeft: 8, fontSize: 11, padding: "2px 6px",
      background: colors[score], color: "white", borderRadius: 4,
      textTransform: "uppercase", letterSpacing: 0.5,
    }}>{score.replace("_", " ")}</span>
  );
}
